(function () {
  // ADR-177 — web dropdown client for the same hybrid-search endpoint the
  // Flutter app calls (cloudflare/assistant-worker, POST /api/v1/search).
  // Fully replaces Shopify's native <predictive-search> fetch (which hit
  // /search/suggest — literal-match only, no Arabic brand transliteration,
  // no semantic layer, no click learning). The container in header.liquid
  // is now a plain [data-eros-hybrid-search] div, not a <predictive-search>
  // custom element, so theme.js's native PredictiveSearch class never
  // upgrades it and never competes for the same fetch/slot.
  if (window.__erosHybridSearchBound) return;
  window.__erosHybridSearchBound = true;

  var BASE_URL = 'https://eros-store-assistant.hayder-5a9.workers.dev';
  var SEARCH_PATH = '/api/v1/search';
  var CLICK_PATH = '/api/v1/search/click';
  var DEBOUNCE_MS = 250;
  // Search-log hygiene (guardian 2026-10-19): every as-you-type request says
  // typing:true and is not logged; the query that stands still for 600 ms is
  // sent once more without it — that is the search the log keeps.
  var SETTLE_MS = 600;
  var MAX_RESULTS = 8;
  var SESSION_KEY = 'eros_search_session_id_v1';

  // Storefront UI language (ADR-191). Shopify sets <html lang="{iso}"> from
  // request.locale, so the first subtag is the active locale. The multilingual
  // index matches regardless, so this only picks the response's localized
  // `title` (used in the degraded fallback below) and tags search analytics.
  function searchLanguage() {
    var lang = (document.documentElement.getAttribute('lang') || '').toLowerCase().split('-')[0];
    return lang === 'ku' || lang === 'en' ? lang : 'ar';
  }

  function sessionId() {
    try {
      var id = window.localStorage.getItem(SESSION_KEY);
      if (!id) {
        id = window.crypto && window.crypto.randomUUID
          ? window.crypto.randomUUID()
          : 's' + Date.now() + Math.random().toString(16).slice(2);
        window.localStorage.setItem(SESSION_KEY, id);
      }
      return id;
    } catch (e) {
      return null;
    }
  }

  function formatMoney(amountDecimalStr) {
    // window.Shopify.formatMoney is never actually defined anywhere in this
    // theme (confirmed live — Shopify's platform-injected `Shopify` global
    // doesn't carry it, and nothing in theme.js/theme.liquid assigns it).
    // Every result silently fell through to a raw (cents/100).toFixed(2),
    // e.g. "15000.00" instead of "15,000 د.ع". The fix — format the
    // moneyFormat/moneyWithCurrencyFormat setting string directly instead of
    // depending on a global that was never actually there — now lives once
    // in eros-commerce-ui.js (window.ErosUI.formatMoneyAmount), which loads
    // earlier and is `defer`red the same as this script, so it has always
    // finished executing by the time this IIFE runs. `card.price` here is a
    // decimal major-unit string, matching formatMoneyAmount's contract.
    return window.ErosUI.formatMoneyAmount(amountDecimalStr);
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function productUrl(handle) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/$/, '') + '/products/' + handle;
  }

  function getPredictiveElements() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-eros-hybrid-search]'));
  }

  function getResultsSlot(predictive) {
    return predictive.querySelector('[slot="results"]');
  }

  function getSearchInput(predictive) {
    var id = predictive.id;
    if (!id) return null;
    var form = document.querySelector('[aria-owns="' + id + '"]');
    return form && form.elements && form.elements.q ? form.elements.q : null;
  }

  function buildPriceHtml(predictive, card) {
    if (!card || card.price == null) return '';
    var saleClass = card.on_sale ? 'text-on-sale' : 'text-subdued';
    var html =
      '<price-list class="price-list">' +
      '<sale-price class="' + saleClass + '">' +
      '<span class="sr-only">' + escapeHtml(predictive.dataset.salePriceLabel) + '</span>' +
      formatMoney(card.price) +
      '</sale-price>';
    if (card.on_sale && card.compare_at_price != null) {
      html +=
        '<compare-at-price class="text-subdued line-through">' +
        '<span class="sr-only">' + escapeHtml(predictive.dataset.regularPriceLabel) + '</span>' +
        formatMoney(card.compare_at_price) +
        '</compare-at-price>';
    }
    html += '</price-list>';
    return html;
  }

  function buildCardHtml(predictive, result, index) {
    var card = result.card;
    // Prefer the live Storefront handle over D1's — a since-renamed product
    // still resolves via product_id server-side, but the OLD handle 404s.
    var handle = (card && card.handle) || result.handle;
    var title = (card && card.title) || result.title || result.title_ar;
    var url = productUrl(handle);
    var linkAttrs =
      'data-instant data-eros-search-result data-position="' + index + '" data-handle="' + escapeHtml(handle) + '"';

    var figureHtml = '';
    if (card && card.image_url) {
      figureHtml =
        '<a href="' + escapeHtml(url) + '" class="horizontal-product-card__figure" ' + linkAttrs + '>' +
        '<img class="horizontal-product-card__image" src="' + escapeHtml(card.image_url) + '" alt="" loading="lazy" width="100" height="100">' +
        '</a>';
    }

    return (
      '<div class="horizontal-product-card">' +
      figureHtml +
      '<div class="horizontal-product-card__info">' +
      '<div class="v-stack gap-1 justify-items-start">' +
      '<a href="' + escapeHtml(url) + '" class="product-title" ' + linkAttrs + '>' + escapeHtml(title) + '</a>' +
      buildPriceHtml(predictive, card) +
      '</div></div></div>'
    );
  }

  function buildResultsHtml(predictive, results) {
    if (results.length === 0) {
      return '<p class="predictive-search__no-results eros-shell-search-results__empty">' +
        escapeHtml(predictive.dataset.noResultsLabel) + '</p>';
    }
    var rows = results
      .slice(0, MAX_RESULTS)
      .map(function (r, i) {
        return buildCardHtml(predictive, r, i);
      })
      .join('');
    return (
      '<div class="predictive-search__results eros-shell-search-results">' +
      '<p class="eros-shell-search-results__heading">' + escapeHtml(predictive.dataset.productsHeading) + '</p>' +
      '<div class="eros-shell-search-results__list">' + rows + '</div>' +
      '<div class="eros-shell-search-results__footer">' +
      '<button type="submit" form="eros-search-form" class="button button--outline w-full">' +
      escapeHtml(predictive.dataset.viewAllLabel) +
      '</button>' +
      '</div></div>'
    );
  }

  function logClick(query, handle, position) {
    try {
      fetch(BASE_URL + CLICK_PATH, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: query,
          handle: handle,
          position: position,
          source: 'web',
          session_id: sessionId(),
        }),
        keepalive: true,
      });
    } catch (e) {
      /* best-effort */
    }
  }

  function bindResultClicks(slot, query) {
    var links = slot.querySelectorAll('[data-eros-search-result]');
    for (var i = 0; i < links.length; i += 1) {
      links[i].addEventListener('click', function (event) {
        var el = event.currentTarget;
        logClick(query, el.getAttribute('data-handle'), Number(el.getAttribute('data-position')));
        // ADR-260 §4.4: a click on a result is an executed search (signed-in only).
        if (window.ErosSearchEvent) window.ErosSearchEvent.record(query);
      });
    }
  }

  function requestBody(query, typing) {
    var body = { query: query, source: 'web', limit: MAX_RESULTS, session_id: sessionId(), language: searchLanguage() };
    if (typing) body.typing = true;
    return body;
  }

  function runSearch(predictive, query, typing) {
    var slot = getResultsSlot(predictive);
    if (!slot) return;

    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start'));
    var requestId = (predictive.__erosRequestId = (predictive.__erosRequestId || 0) + 1);

    fetch(BASE_URL + SEARCH_PATH, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody(query, typing)),
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (data) {
        if (predictive.__erosRequestId !== requestId) return; // superseded by a newer keystroke
        var results = (data && data.ok && data.results) || [];
        slot.innerHTML = buildResultsHtml(predictive, results);
        bindResultClicks(slot, query);
      })
      .catch(function () {
        if (predictive.__erosRequestId !== requestId) return;
        slot.innerHTML = '';
      })
      .then(function () {
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end'));
      });
  }

  function debounce(fn, ms) {
    var timer;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(ctx, args);
      }, ms);
    };
  }

  function bindPredictive(predictive) {
    if (predictive.__erosHybridBound) return;
    predictive.__erosHybridBound = true;

    var input = getSearchInput(predictive);
    if (!input) return;

    var debouncedSearch = debounce(function () {
      var query = input.value.trim();
      if (!query) {
        var slot = getResultsSlot(predictive);
        if (slot) slot.innerHTML = '';
        return;
      }
      runSearch(predictive, query, true);
    }, DEBOUNCE_MS);

    // The settled query, once per distinct text: the one the search log keeps.
    var settledSearch = debounce(function () {
      var query = input.value.trim();
      if (!query || query === predictive.__erosSettledQuery) return;
      predictive.__erosSettledQuery = query;
      runSearch(predictive, query, false);
    }, SETTLE_MS);

    input.addEventListener('input', debouncedSearch);
    input.addEventListener('input', settledSearch);
  }

  window.ErosHybridSearch = { requestBody: requestBody, SETTLE_MS: SETTLE_MS };

  function init() {
    getPredictiveElements().forEach(bindPredictive);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
